/* @flow */

import { detect as detectBrowser } from 'detect-browser';

import type { DmsSettingMap, NETGEM_API_DMS_DEVICE_INFO_SETTINGS } from '../../libs/netgemLibrary/dms/types/DeviceInfoSettings';
import type { KeyValuePair } from '../jsHelpers/types';

import { Drm, getFirstSupportedDrm } from '../jsHelpers/Drm';
import { capitalizeFirstLetter } from '../jsHelpers/string';
import Localizer from '../localization';

const getDeviceAndBrowser: () => {
  browserVersion: string,
  deviceName: string,
} = () => {
  let deviceName = Localizer.localize('authentication.my_device');
  let browserVersion = '';

  const browser = detectBrowser();
  if (browser) {
    browserVersion = browser.version;

    if (browser.name && browser.os) {
      deviceName = Localizer.localize('authentication.browser_and_os', {
        browser: capitalizeFirstLetter(browser.name),
        os: capitalizeFirstLetter(browser.os),
      });
    } else if (browser.name) {
      deviceName = capitalizeFirstLetter(browser.name);
    }
  }

  return {
    browserVersion,
    deviceName,
  };
};

const getDeviceOS: () => string = () => {
  const drm = getFirstSupportedDrm();

  if (drm !== undefined) {
    switch (drm) {
      case Drm.Fairplay:
        return process.env.REACT_APP_DRM_FAIRPLAY_NAME ?? 'dsktp-fairplay';
      case Drm.Playready:
        return process.env.REACT_APP_DRM_PLAYREADY_NAME ?? 'dsktp-playready';
      case Drm.Widevine:
        return process.env.REACT_APP_DRM_WIDEVINE_NAME ?? 'dsktp-widevine';

      // No default
    }
  }

  // Default value needs to be set because of Google bots
  return process.env.REACT_APP_DRM_WIDEVINE_NAME ?? 'dsktp-widevine';
};

const addGtmData: (params: KeyValuePair<string>) => void = (params) => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  if (utmSource) {
    params.utm_source = utmSource;
  }

  if (utmMedium) {
    params.utm_medium = utmMedium;
  }

  if (utmCampaign) {
    params.utm_campaign = utmCampaign;
  }
};

const buildDeviceUrl: (deviceId: string, url?: string) => ?string = (deviceId, url) => {
  if (!url || deviceId === '') {
    return null;
  }

  const { browserVersion, deviceName } = getDeviceAndBrowser();

  const params: KeyValuePair<string> = {
    appversion: '',
    deviceid: deviceId,
    devicemodel: 'html5',
    devicename: deviceName,
    deviceos: getDeviceOS(),
    deviceosversion: browserVersion,
    webview: 'false',
  };

  addGtmData(params);
  Localizer.addLanguageCode(params);

  const localUrl = new URL(url);
  Object.entries(params).forEach(([key, value]) => localUrl.searchParams.set(key, value));

  return localUrl.toString();
};

const areSectionSettingsEqual: (settings1: Array<NETGEM_API_DMS_DEVICE_INFO_SETTINGS>, settings2: Array<NETGEM_API_DMS_DEVICE_INFO_SETTINGS>) => boolean = (settings1, settings2) => {
  if (settings1.length !== settings2.length) {
    return false;
  }

  const map1 = new Map(settings1.map((s) => [s.name, s.value]));
  const map2 = new Map(settings2.map((s) => [s.name, s.value]));
  const keys1 = map1.keys();

  for (const key of keys1) {
    const val1 = map1.get(key);
    const val2 = map2.get(key);
    if (val2 !== val1) {
      return false;
    }
  }

  return true;
};

const areSettingsEqual: (settings1: ?DmsSettingMap, settings2: ?DmsSettingMap) => boolean = (settings1, settings2) => {
  if (settings1 === settings2) {
    return true;
  }

  if (!settings1 || !settings2) {
    return false;
  }

  const keys1 = Object.keys(settings1);
  const keys2 = Object.keys(settings2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = settings1[key];
    const val2 = settings2[key];
    if (!Array.isArray(val1) || !Array.isArray(val2)) {
      return false;
    }

    if (!areSectionSettingsEqual(val1, val2)) {
      return false;
    }
  }

  return true;
};

export { areSettingsEqual, buildDeviceUrl, getDeviceOS };
